import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';

import {
  Alert,
  Switch,
  TextField,
  ToggleButton,
} from '@mui/material';
import { DatePicker } from '@mui/lab';

import { formatRecurrenceSummary } from '../../../../../../utils/recurrence';

import {
  Container,
  Section,
  Label,
  ToggleRow,
  StyledToggleButtonGroup,
  DayToggleButtonGroup,
} from './styles';
import texts from './texts.json';

const DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
const INTERVAL_OPTIONS = [
  { value: 1, label: texts.weekly },
  { value: 2, label: texts.biweekly },
  { value: 4, label: texts.every4Weeks },
];

const RecurrenceBlock = ({
  isRecurring,
  onIsRecurringChange,
  interval,
  onIntervalChange,
  byDay,
  onByDayChange,
  endDate,
  onEndDateChange,
  startDate,
  timeZone,
  nextFireMoment,
}) => {
  const summary = isRecurring && byDay.length
    ? formatRecurrenceSummary({ interval, byDay })
    : null;

  return (
    <Container>
      <ToggleRow
        labelPlacement="start"
        control={(
          <Switch
            checked={isRecurring}
            onChange={(e) => onIsRecurringChange(e.target.checked)}
            size="small"
          />
        )}
        label={summary || texts.recurringReminder}
      />

      {isRecurring && (
        <Section>
          <Label>{texts.repeatEvery}</Label>
          <StyledToggleButtonGroup
            value={interval}
            exclusive
            onChange={(_, next) => next != null && onIntervalChange(next)}
            size="small"
          >
            {INTERVAL_OPTIONS.map(({ value, label }) => (
              <ToggleButton key={value} value={value}>{label}</ToggleButton>
            ))}
          </StyledToggleButtonGroup>

          <Label>{texts.onTheseDays}</Label>
          <DayToggleButtonGroup
            value={byDay}
            onChange={(_, next) => onByDayChange(next)}
            size="small"
          >
            {DAYS.map((day) => (
              <ToggleButton key={day} value={day}>
                {texts.days[day]}
              </ToggleButton>
            ))}
          </DayToggleButtonGroup>

          <Label>{texts.ends}</Label>
          <DatePicker
            value={endDate}
            onChange={onEndDateChange}
            minDate={startDate || moment().local()}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                inputProps={{
                  ...params.inputProps,
                  placeholder: texts.endsPlaceholder,
                }}
              />
            )}
          />

          {nextFireMoment && (
            <Alert severity="info">
              {`${texts.nextReminder} ${nextFireMoment.clone().tz(timeZone).format('ddd, MMM D, h:mm A')}`}
            </Alert>
          )}
        </Section>
      )}
    </Container>
  );
};

RecurrenceBlock.propTypes = {
  isRecurring: PropTypes.bool.isRequired,
  onIsRecurringChange: PropTypes.func.isRequired,
  interval: PropTypes.number.isRequired,
  onIntervalChange: PropTypes.func.isRequired,
  byDay: PropTypes.arrayOf(PropTypes.string).isRequired,
  onByDayChange: PropTypes.func.isRequired,
  endDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  onEndDateChange: PropTypes.func.isRequired,
  startDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  timeZone: PropTypes.string.isRequired,
  nextFireMoment: PropTypes.object,
};

RecurrenceBlock.defaultProps = {
  endDate: null,
  startDate: null,
  nextFireMoment: null,
};

export default RecurrenceBlock;
